import React from 'react';

import {
    DebtLabelEnum,
    DebtTypeEnum,
    creditCardOrMailOrderFormItems,
    newDebtFormItems,
    securedFormItems,
    sharedFormItems,
} from '@src/app/formModels/eligibility/debtCon';
import { CategoryPortal, Declaration } from '@src/app/interfaces/debtcon';
import BooksIcon from '@src/ui/components/Icons/BooksIcon';
import CarIcon from '@src/ui/components/Icons/CarIcon';
import CreditCardIcon from '@src/ui/components/Icons/CreditCardIcon';
import HomeIcon from '@src/ui/components/Icons/HomeIcon';
import OverdraftIcon from '@src/ui/components/Icons/OverdraftIcon';
import PersonalLoanIcon from '@src/ui/components/Icons/PersonalLoanIcon';
import SandTimerIcon from '@src/ui/components/Icons/SandTimerIcon';

export interface CurrentDebt {
    type: string;
    categoryPortal: CategoryPortal;
}

export type DebtType = DebtLabelEnum;

export const debtTypeConfig: Partial<
    Record<DebtLabelEnum, { icon: React.ReactElement; subtext?: string }>
> = {
    [DebtLabelEnum.CreditCard]: {
        icon: <CreditCardIcon />,
        subtext: 'Including store cards',
    },
    [DebtLabelEnum.PersonalLoan]: {
        icon: <PersonalLoanIcon />,
    },
    [DebtLabelEnum.Overdraft]: {
        icon: <OverdraftIcon />,
        subtext: 'Overdraft used on a current account.',
    },
    [DebtLabelEnum.HirePurchase]: {
        icon: <CarIcon />,
        subtext: 'Or Conditional Sale. Often used to buy a car or furniture.',
    },
    [DebtLabelEnum.ShoppingAccount]: {
        icon: <BooksIcon />,
        subtext:
            'Also known as Catalogue Credit. Often used to buy clothes or other items via post or online. Payments are usually spread over weekly or monthly instalments.',
    },
    [DebtLabelEnum.FixedTermCreditSale]: {
        icon: <SandTimerIcon />,
        subtext:
            'Often used to spread out payments when buying something. Sometimes includes an interest-free offer.',
    },
    [DebtLabelEnum.Mortgage]: {
        icon: <HomeIcon />,
        subtext:
            'First charge mortgage. This includes second homes or buy to let properties for example.',
    },
    [DebtLabelEnum.SecondMortgage]: {
        icon: <HomeIcon />,
        subtext: 'Second charge mortgage on a property you own.',
    },
    [DebtLabelEnum.SecuredLoan]: {
        icon: <HomeIcon />,
    },
    [DebtLabelEnum.ResidentialMortgage]: {
        icon: <HomeIcon />,
    },
    [DebtLabelEnum.BuyToLetMortgage]: {
        icon: <HomeIcon />,
    },
};

export const getFormItems = (
    item: {
        type: string;
        categoryPortal: CategoryPortal;
        declaration: Declaration;
    },
    applicants: any,
    creditCardOrMailOrder: boolean,
): any => {
    return item.declaration === Declaration.User
        ? newDebtFormItems(applicants, item, creditCardOrMailOrder)
        : item.categoryPortal === CategoryPortal.MortgagesAndSecured
        ? securedFormItems(item.categoryPortal)
        : unsecuredFormItems(item, creditCardOrMailOrder);
};

const unsecuredFormItems = (
    currentDebt: CurrentDebt,
    creditCardOrMailOrder: boolean,
) =>
    creditCardOrMailOrder
        ? creditCardOrMailOrderFormItems(currentDebt)
        : sharedFormItems(currentDebt.categoryPortal);

export const getApplicantName = (
    numberOfApplicants: number,
    currentDebt: { joint?: boolean; primaryApplicant: boolean },
    applicants: { firstName: string; lastName: string }[],
): string | undefined => {
    if (numberOfApplicants === 1) {
        return undefined;
    }

    return currentDebt.joint
        ? 'Joint'
        : `${applicants[0].firstName} ${applicants[0].lastName}`;
};

// we transform the type to label but this method is called once before the transformation so we need to check both
export const isCreditCardOrMailOrder = (type: string): boolean =>
    type === DebtTypeEnum.CreditCard ||
    type === DebtTypeEnum.MailOrder ||
    type === DebtLabelEnum.CreditCard ||
    type === DebtLabelEnum.ShoppingAccount;
